/** @jsxImportSource @emotion/react */
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import BigNumber from 'bignumber.js';
import { ConnectWallet, Delimiter, Icon, Tooltip, ButtonGroup, PrimaryButton } from 'components';
import React, { useMemo } from 'react';
import { useTranslation } from 'translation';

import useSuccessfulTransactionModal from 'hooks/useSuccessfulTransactionModal';
import { useSetInviter } from 'clients/api';
import DelegateModal from '../DelegateModal';
import { useStyles } from './styles';

export interface IVotingWalletUiProps {
  connectedWallet: boolean;
  openAuthModal: () => void;
  currentUserAccountAddress: string | undefined;
  delegateModelIsOpen: boolean;
  setDelegateModelIsOpen: (isOpen: boolean) => void;
  currentUserDelegateAddress: string | undefined;
  isLoading: boolean;
  delegateModelAddress: string;
  setDelegateModelAddress: (address: string) => void;
  vaultAddress: string;
  allUsers: any;
  networkStats: any;
  directReferralCount: any;
  referrerInfo: any;
  allUsersInNetwork: any;
  networkRewardDetails: any;
  setVoteDelegationMutation: (params: { inviterAddress: string; accountAddress: string }) => Promise<any>;
  isVoteDelegationLoading: boolean;
  delegate: any;
}

const VotingWalletUi: React.FC<IVotingWalletUiProps> = ({
  connectedWallet,
  openAuthModal,
  currentUserAccountAddress,
  delegateModelIsOpen,
  setDelegateModelIsOpen,
  currentUserDelegateAddress,
  isLoading,
  delegateModelAddress,
  setDelegateModelAddress,
  vaultAddress,
  allUsers,
  networkStats,
  directReferralCount,
  referrerInfo,
  allUsersInNetwork,
  networkRewardDetails,
  setVoteDelegationMutation,
  isVoteDelegationLoading,
  delegate,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();

  // 计算层级数据
  const levelData = useMemo(() => {
    if (!networkRewardDetails?.usersByLevel || !networkRewardDetails?.rewardsByLevel) {
      return [];
    }

    return networkRewardDetails.usersByLevel
      .map((userCount: number, index: number) => ({
        level: index + 1,
        userCount,
        reward: networkRewardDetails.rewardsByLevel[index] || new BigNumber(0),
      }))
      .filter((item: any) => item.userCount > 0);
  }, [networkRewardDetails]);

  // 计算网络总览数据
  const networkOverview = useMemo(
    () => ({
      totalUsers: allUsersInNetwork?.totalCount || 0,
      directReferrals: directReferralCount?.count || 0,
      activeStakers: networkStats?.activeStakers || 0,
      totalReward: networkRewardDetails?.totalNetworkReward || new BigNumber(0),
      potentialReward: networkRewardDetails?.potentialReward || new BigNumber(0),
    }),
    [allUsersInNetwork, directReferralCount, networkStats, networkRewardDetails],
  );

  // 检查是否已绑定推荐人
  const hasReferrer = useMemo(() =>
    referrerInfo?.referrer && referrerInfo.referrer !== '0x0000000000000000000000000000000000000000',
    [referrerInfo?.referrer],
  );

  const { openSuccessfulTransactionModal } = useSuccessfulTransactionModal();

  if (!connectedWallet) {
    return <ConnectWallet message={t('vote.votingWallet.connectWalletMessage')} />;
  }

  return (
    <div css={styles.root}>
      <Paper css={styles.paper}>
        {/* 网络总览卡片 */}
        <div css={styles.headerSection}>
          <Typography variant="h6" css={styles.title}>
            {t('vote.votingWallet.networkOverview')}
          </Typography>

          <div css={styles.overviewGrid}>
            {/* 总网络规模 */}
            <div css={[styles.statCard, styles.gradientPrimary]}>
              <div css={styles.statIcon}>
                <Icon name="person" />
              </div>
              <div css={styles.statContent}>
                <Typography variant="h2" css={styles.statNumber}>
                  {networkOverview.totalUsers}
                </Typography>
                <Typography variant="small2" css={styles.statLabel}>
                  {t('vote.votingWallet.totalNetworkSize')}
                </Typography>
              </div>
            </div>

            {/* 直推人数 */}
            <Card css={[styles.statCard, styles.gradientSecondary]}>
              <div css={styles.statIcon}>
                <Icon name="person" />
              </div>
              <div css={styles.statContent}>
                <Typography variant="h2" css={styles.statNumber}>
                  {networkOverview.directReferrals}
                </Typography>
                <Typography variant="small2" css={styles.statLabel}>
                  {t('vote.votingWallet.directReferrals')}
                </Typography>
              </div>
            </Card>

            {/* 活跃质押者 */}
            <div css={[styles.statCard, styles.gradientTertiary]}>
              <div css={styles.statIcon}>
                <Icon name="lightening" />
              </div>
              <div css={styles.statContent}>
                <Typography variant="h2" css={styles.statNumber}>
                  {networkOverview.activeStakers}
                </Typography>
                <Typography variant="small2" css={styles.statLabel}>
                  {t('vote.votingWallet.activeStakers')}
                </Typography>
              </div>
            </div>

            {/* 累计收益 */}
            <div css={[styles.statCard, styles.gradientQuaternary]}>
              <div css={styles.statIcon}>
                <Icon name="UMI" />
              </div>
              <div css={styles.statContent}>
                <Typography variant="h2" css={styles.statNumber}>
                  {new BigNumber(networkOverview.totalReward)
                    .dividedBy(new BigNumber(10).pow(18))
                    .toFixed(2)}
                </Typography>
                <Typography variant="small2" css={styles.statLabel}>
                  {t('vote.votingWallet.totalNetworkReward')} USDT
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <Delimiter css={styles.delimiter} />

        {/* 层级分布展示 */}
        <div css={styles.levelSection}>
          <Typography variant="h6" css={styles.sectionTitle}>
            {t('vote.votingWallet.levelDistribution')}
          </Typography>

          {levelData.length > 0 ? (
            <div css={styles.levelGrid}>
              {levelData.map((item: any) => (
                <div key={item.level} css={styles.levelCard}>
                  <div css={styles.levelHeader}>
                    <Typography variant="h6" css={styles.levelTitle}>
                      L{item.level}
                    </Typography>
                    <Tooltip title={t('vote.votingWallet.levelTooltip', { level: item.level })}>
                      <Icon name="info" css={styles.infoIcon} />
                    </Tooltip>
                  </div>

                  <div css={styles.levelStats}>
                    <div css={styles.levelStat}>
                      <Typography variant="h4" css={styles.levelNumber}>
                        {item.userCount}
                      </Typography>
                      <Typography variant="small2" css={styles.levelLabel}>
                        {t('vote.votingWallet.users')}
                      </Typography>
                    </div>

                    <div css={styles.levelStat}>
                      <Typography variant="h4" css={styles.levelNumber}>
                        {new BigNumber(item.reward).dividedBy(new BigNumber(10).pow(18)).toFixed(2)}
                      </Typography>
                      <Typography variant="small2" css={styles.levelLabel}>
                        USDT {t('vote.votingWallet.reward')}
                      </Typography>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div css={styles.emptyState}>
              <Icon name="person" css={styles.emptyIcon} />
              <Typography variant="body1" css={styles.emptyText}>
                {t('vote.votingWallet.noNetworkData')}
              </Typography>
              <Typography variant="small2" css={styles.emptySubtext}>
                {t('vote.votingWallet.buildYourNetwork')}
              </Typography>
            </div>
          )}
        </div>

        <Delimiter css={styles.delimiter} />

        {/* 推荐关系与操作 */}
        <div css={styles.actionSection}>
          <div css={styles.referrerSection}>
            <Typography variant="h6" css={styles.sectionTitle}>
              {t('vote.votingWallet.referrerRelationship')}
            </Typography>

            {hasReferrer ? (
              <div css={styles.referrerCard}>
                <div css={styles.referrerInfo}>
                  <Icon name="person" css={styles.referrerIcon} />
                  <div>
                    <Typography variant="body1" css={styles.referrerLabel}>
                      {t('vote.votingWallet.yourReferrer')}
                    </Typography>
                    <Typography variant="small1" css={styles.referrerAddress}>
                      {referrerInfo.referrer.slice(0, 6)}...{referrerInfo.referrer.slice(-4)}
                    </Typography>
                  </div>
                </div>
                <Icon name="checked" css={styles.verifiedIcon} />
              </div>
            ) : (
              <div css={styles.noReferrerCard}>
                <Icon name="person" css={styles.noReferrerIcon} />
                <Typography variant="body2" css={styles.noReferrerText}>
                  {t('vote.votingWallet.noReferrer')}
                </Typography>
              </div>
            )}
          </div>


          {/* 费用选择器与操作按钮 */}
          <div css={styles.feeSection}>
            <PrimaryButton
              onClick={() => setDelegateModelIsOpen(true)}
              disabled={isLoading || hasReferrer}
              fullWidth
              css={styles.buttonGroup}
            >
              {hasReferrer
                ? t('vote.votingWallet.alreadyBound')
                : t('vote.votingWallet.setDelegate')
              }
            </PrimaryButton>
          </div>
        </div>

        {/* 潜在收益提示 */}
        {networkOverview.potentialReward.isGreaterThan(0) && (
          <div css={styles.potentialRewardBanner}>
            <Icon name="arrowUp" css={styles.trendingIcon} />
            <div css={styles.bannerContent}>
              <Typography variant="body1" css={styles.bannerTitle}>
                {t('vote.votingWallet.potentialReward')}
              </Typography>
              <Typography variant="small2" css={styles.bannerText}>
                {t('vote.votingWallet.potentialRewardText', {
                  amount: new BigNumber(networkOverview.potentialReward)
                    .dividedBy(new BigNumber(10).pow(18))
                    .toFixed(2),
                })}
              </Typography>
            </div>
          </div>
        )}
      </Paper>

      <DelegateModal
        isOpen={delegateModelIsOpen}
        onClose={() => setDelegateModelIsOpen(false)}
        currentUserAccountAddress={currentUserAccountAddress}
        previouslyDelegated={false}
        setVoteDelegation={(params) =>
          setVoteDelegationMutation({
            inviterAddress: params.inviterAddress,
            accountAddress: currentUserAccountAddress || '',
          })
        }
        isVoteDelegationLoading={isLoading}
        setDelegateModelAddress={() => {}}
      />
    </div>
  );
};

export default VotingWalletUi;
